/**
 * useLiveLocationRefresh — keep a sharer's live location fresh for peers, so it
 * doesn't silently expire from their Nearby (24h — get_connection_locations,
 * migration 047). Refreshes on app open and whenever the app becomes active;
 * `updateLiveLocation`'s 5-min guard keeps the extra triggers cheap.
 *
 * On open the sharing flag is reconciled from the DB first — the localStorage
 * flag can drift false while the DB says sharing (location-refresh-rearm). The
 * foreground handlers register whatever the flag says and re-check it per fire.
 *
 * THREE events, because no single one is reliable across platforms:
 * visibilitychange (PWA background→foreground), focus (laptop lid/sleep resume
 * — often skips visibilitychange), and pageshow (bfcache restore).
 *
 * @param {string} userType - 'profile' | 'anon' | …
 */

import { useEffect } from 'react';
import {
  getLocationSharingEnabled,
  hydrateSharingFromDb,
  updateLiveLocation,
} from '../services/location/liveLocationService';
import { authReady } from '../services/supabase';
import { logConnectionError } from '../services/analytics/eventLogger';
import { isFullTier } from '../utils/userTier';
import { getProfileUserId } from '../utils/identityUtils';

export default function useLiveLocationRefresh(userType) {
  useEffect(() => {
    // Full-only: live-location powers Nearby, a Full feature.
    if (userType !== 'profile' || !isFullTier()) return;
    const profileUserId = getProfileUserId();
    if (!profileUserId) return;

    let cancelled = false;

    // App open — after auth is ready, reconcile the flag with the DB truth,
    // then refresh if sharing.
    authReady
      .then(() => hydrateSharingFromDb(profileUserId))
      .then(({ enabled, error }) => {
        // No-Fallbacks exception (discussed, see hydrateSharingFromDb): on a
        // read error the last local value is kept — logged, never silent.
        if (error) {
          logConnectionError('location_sharing_read_failed', {
            stage: 'app_open_hydrate',
            profileUserId,
            error,
          });
        }
        if (!cancelled && enabled) updateLiveLocation(profileUserId);
      });

    const refreshIfSharing = () => {
      if (getLocationSharingEnabled()) updateLiveLocation(profileUserId);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshIfSharing();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', refreshIfSharing);
    window.addEventListener('pageshow', refreshIfSharing);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', refreshIfSharing);
      window.removeEventListener('pageshow', refreshIfSharing);
    };
  }, [userType]);
}
